/**
 * APP — public application services.
 *
 * This is the ONLY thing the UI is allowed to import from below the
 * presentation layer. Under the hood it delegates to engines / portfolio.
 *
 * Rule for Claude Code: never call ports directly from a component.
 * Add a new method here, then implement it via engines/portfolio.
 */
import { container } from "./container";
import { bootstrapDemo } from "./seed";

// Kick off demo seed at import time. Idempotent.
// If seed fails for any reason, we log the error and continue — the app
// must remain usable even with an empty world. Never let `ready` reject,
// because withReady() awaits it and a rejection would cascade into every
// UI hook.
const ready = bootstrapDemo().catch((err) => {
  // eslint-disable-next-line no-console
  console.error("[commercial-operator] demo bootstrap failed:", err);
});

function withReady(fn) {
  return async (...args) => {
    await ready;
    return fn(...args);
  };
}

export const app = {
  // -------- Home / Executive --------
  home: {
    listEngineSummaries: withReady(() => container.portfolio.listEngineSummaries()),
    countActors: withReady(() => container.portfolio.countActors()),
  },

  // -------- Wallet --------
  wallet: {
    listOpportunities: withReady((filter = {}) =>
      container.portfolio.listAllOpportunities(filter)
    ),
  },

  // -------- Per-engine access --------
  engine(engineId) {
    const e = container.engines[engineId];
    if (!e) throw new Error(`Unknown engine '${engineId}'`);
    return {
      listOpportunities: withReady((filter) => e.listOpportunities(filter)),
      getMetrics: withReady(() => e.getMetrics()),
      getSummary: withReady(() => e.getSummary()),
    };
  },

  // -------- Opportunity operations (engine-aware routing) --------
  opportunity: {
    get: withReady(async (id) => {
      // route to the engine that owns this opp
      const opp = await container.ports.storage.getOpportunity(id);
      if (!opp) return null;
      const engine = container.engines[opp.engineId];
      return engine.getOpportunity(id);
    }),
    recordOutcome: withReady(async (id, payload) => {
      const opp = await container.ports.storage.getOpportunity(id);
      if (!opp) throw new Error("Not found");
      return container.engines[opp.engineId].recordOutcome(id, payload);
    }),
    updatePlayBody: withReady(async (id, suggestedBody) => {
      const opp = await container.ports.storage.getOpportunity(id);
      if (!opp) throw new Error("Not found");
      return container.engines[opp.engineId].updatePlayBody(id, suggestedBody);
    }),
    regeneratePlay: withReady(async (id) => {
      const opp = await container.ports.storage.getOpportunity(id);
      if (!opp) throw new Error("Not found");
      return container.engines[opp.engineId].regeneratePlay(id);
    }),
    sendPlay: withReady(async (id, { channel }) => {
      const opp = await container.ports.storage.getOpportunity(id);
      if (!opp) throw new Error("Not found");
      const actor = await container.ports.storage.getActor(opp.actorId);
      const play = await container.ports.storage.getLatestPlay(id);
      const res = await container.ports.messaging.send({
        actor, channel, body: play.suggestedBody,
      });
      // Un envío real ocurrió — el estado de respuesta deja de ser
      // "sin contactar" sin que nadie tenga que marcarlo a mano.
      if (res?.ok && opp.responseStatus === "not_contacted") {
        await container.ports.storage.updateOpportunity(id, { responseStatus: "awaiting" });
      }
      return res;
    }),
    // Marca manual de que un cliente respondió — necesaria mientras no exista
    // un canal real conectado con webhook de entrada (WhatsApp/SMS/Email).
    // Cuando exista, esto se dispara solo desde el adapter de mensajería; el
    // método queda igual, solo cambia quién lo llama.
    updateResponseStatus: withReady(async (id, responseStatus) =>
      container.ports.storage.updateOpportunity(id, { responseStatus })
    ),
  },

  // -------- Cross-engine --------
  closures: {
    list: withReady((filter) => container.portfolio.listClosures(filter)),
  },
  team: {
    list: withReady(() => container.portfolio.listTeamMembers()),
  },
  forecast: {
    get: withReady(() => container.portfolio.getForecast()),
  },

  // -------- Ingestion --------
  ingestion: {
    ingestActorDrafts: withReady((drafts) => container.portfolio.ingestActors(drafts)),
  },

  // -------- Integrations status (for /settings & /ingestion) --------
  integrations: {
    list: withReady(async () => {
      const [crmS, llmS, kS, calS, erpS] = await Promise.all([
        container.ports.crm.status(),
        container.ports.llm.status(),
        container.ports.knowledge.status(),
        container.ports.calendar.status(),
        container.ports.erp.status(),
      ]);
      const msgChannels = await container.ports.messaging.availableChannels();
      const msgConnected = msgChannels.length > 0;
      return [
        { id: "crm", label: `CRM (${crmS.vendor})`, category: "crm",
          status: crmS.status },
        { id: "messaging", label: `Mensajería (${msgConnected ? "activa" : "sin canal"})`,
          category: "channel", status: msgConnected ? "connected" : "not_connected" },
        { id: "llm", label: `Motor de IA (${llmS.vendor})`, category: "intelligence",
          status: llmS.status === "ready" ? "connected" : "not_connected" },
        { id: "knowledge", label: "Product Bible", category: "knowledge",
          status: kS.status === "ready" ? "connected" : "not_connected" },
        { id: "calendar", label: `Calendario (${calS.vendor})`, category: "calendar",
          status: calS.status },
        { id: "erp", label: `ERP (${erpS.vendor})`, category: "erp",
          status: erpS.status },
      ];
    }),
    connect: withReady(async (id) => {
      if (id === "crm") return container.ports.crm.connect();
      throw new Error("INTEGRATION_CONNECT_NOT_IMPLEMENTED_FOR_" + id);
    }),
  },

  // -------- Read-only introspection of the port bindings (for /settings) --------
  config: {
    portBindings: () => ({ ...container.config }),
  },
};
